import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { ChevronLeft, ChevronRight, Eye, Download, X } from "lucide-react";
import axiosInstance from "../../axiosInstance";
import GeneralTable from "../../components/Table/GeneralTable";
import PageHeader from "../../components/ui/PageHeader";
import useModalA11y from "../../components/ui/useModalA11y";
import { parseLocalDate } from "./leaveUtils";
import "./Payroll.scss";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MIN_YEAR = 2000;
const MAX_YEAR = 2100;

const payslipLabel = (payrollDate) => {
  const date = parseLocalDate(payrollDate);
  return date
    ? date.toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : payrollDate;
};

// The payslip HTML comes from the server. It is shown inside a sandboxed
// iframe (no scripts, isolated styles) instead of being injected into the page.
const PayslipViewer = ({ payslip, busy, onClose, onDownload }) => {
  const panelRef = useRef(null);
  const closeRef = useRef(null);

  useModalA11y({
    open: Boolean(payslip),
    onClose,
    containerRef: panelRef,
    initialFocusRef: closeRef,
  });

  if (!payslip) return null;

  return createPortal(
    <div className="payslip-root">
      <div className="payslip-overlay" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className="payslip-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="payslip-title"
        tabIndex={-1}
      >
        <header className="payslip-modal__header">
          <h2 id="payslip-title">Payslip — {payslipLabel(payslip.date)}</h2>
          <button
            ref={closeRef}
            type="button"
            className="hr-iconbtn"
            aria-label="Close payslip"
            onClick={onClose}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="payslip-modal__body">
          {payslip.html ? (
            <iframe
              title={`Payslip ${payslipLabel(payslip.date)}`}
              className="payslip-frame"
              sandbox=""
              srcDoc={String(payslip.html)}
            />
          ) : (
            <p className="payslip-empty">This payslip has no preview.</p>
          )}
        </div>

        <footer className="payslip-modal__footer">
          <button
            type="button"
            className="hr-btn hr-btn--primary"
            onClick={() => onDownload(payslip.date)}
            disabled={busy}
          >
            <Download size={15} aria-hidden="true" />
            Download PDF
          </button>
          <button
            type="button"
            className="hr-btn hr-btn--secondary"
            onClick={onClose}
          >
            Close
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

const EmployeePayroll = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [viewingPayslip, setViewingPayslip] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleView = async (payrollDate) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(
        `/api/emp/payslip/view/${payrollDate}`,
      );

      // The HTML is in the nested data property of the API response
      setViewingPayslip({ date: payrollDate, html: response.data.data });
      toast.success("Payslip loaded successfully!");
    } catch (error) {
      console.error("Failed to view payslip", error);
      toast.error("Couldn't load this payslip. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (payrollDate) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/payslip/download/${payrollDate}`,
      );

      // Base64 string (JVBERi0x...)
      const base64Pdf = response.data.data || response.data;

      // Decode Base64 → binary
      const binary = atob(base64Pdf);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Payslip-${payrollDate}.pdf`;
      link.click();

      URL.revokeObjectURL(url);
      toast.success("Payslip downloaded successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't download this payslip. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ---- table ----------------------------------------------------------------
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const rows = MONTHS.map((name, index) => {
    const number = index + 1;
    return {
      month: name,
      monthNumber: number,
      year: selectedYear,
      payrollDate: `${selectedYear}-${String(number).padStart(2, "0")}-01`,
      isFuture:
        selectedYear > currentYear ||
        (selectedYear === currentYear && number > currentMonth),
    };
  });

  const columns = [
    { header: "Month", accessor: "month", sortAccessor: (r) => r.monthNumber },
    { header: "Year", accessor: "year" },
    {
      header: "Actions",
      key: "actions",
      sortable: false,
      align: "right",
      render: (row) => (
        <div className="payroll-actions">
          <button
            type="button"
            className="hr-btn hr-btn--secondary payroll-btn"
            disabled={row.isFuture || isLoading}
            aria-label={`View payslip for ${row.month} ${row.year}`}
            onClick={() => handleView(row.payrollDate)}
          >
            <Eye size={14} aria-hidden="true" />
            View
          </button>
          <button
            type="button"
            className="hr-btn hr-btn--secondary payroll-btn"
            disabled={row.isFuture || isLoading}
            aria-label={`Download payslip for ${row.month} ${row.year}`}
            onClick={() => handleDownload(row.payrollDate)}
          >
            <Download size={14} aria-hidden="true" />
            Download
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="payroll-page">
      <PageHeader
        breadcrumbs={[
          { label: "Payroll", to: "/ess/payroll" },
          { label: "My Payslips" },
        ]}
        title="My Payslips"
        subtitle="View or download your payslip for any past month."
      />

      <section className="hr-card payroll-year" aria-labelledby="year-heading">
        <div>
          <h2 id="year-heading">Select year</h2>
          <p>Payslips for future months aren't available yet.</p>
        </div>

        <div className="payroll-year__controls">
          <button
            className="hr-iconbtn payroll-year__arrow"
            aria-label="Previous year"
            type="button"
            disabled={selectedYear <= MIN_YEAR}
            onClick={() => setSelectedYear((y) => Math.max(MIN_YEAR, y - 1))}
          >
            <ChevronLeft size={18} aria-hidden="true" />
          </button>
          <input
            id="year-input"
            type="number"
            min={MIN_YEAR}
            max={MAX_YEAR}
            value={selectedYear}
            aria-label="Year"
            className="hr-input payroll-year__input"
            placeholder="YYYY"
            onChange={(e) => {
              const year = parseInt(e.target.value, 10);
              if (!isNaN(year) && year > 0 && year <= 9999)
                setSelectedYear(year);
            }}
          />
          <button
            className="hr-iconbtn payroll-year__arrow"
            aria-label="Next year"
            type="button"
            disabled={selectedYear >= MAX_YEAR}
            onClick={() => setSelectedYear((y) => Math.min(MAX_YEAR, y + 1))}
          >
            <ChevronRight size={18} aria-hidden="true" />
          </button>
        </div>
      </section>

      <GeneralTable
        columns={columns}
        data={rows}
        rowKey={(row) => row.payrollDate}
        title={`${selectedYear} Payslips`}
        initialPageSize={12}
        showPagination={false}
      />

      <PayslipViewer
        payslip={viewingPayslip}
        busy={isLoading}
        onClose={() => setViewingPayslip(null)}
        onDownload={handleDownload}
      />
    </div>
  );
};

export default EmployeePayroll;
